/**
 * A bounded group of related content on a screen: a title, optional actions beside it, the body,
 * and an optional footer. It has no shadow, because a card on a data screen is a boundary and not
 * an object. `interactive` makes the whole card one link, with the title as its name.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useId } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { focusRing } from "./focus.ts";
import { ChevronRightIcon } from "./icons.tsx";
import type { Navigate } from "./Link.tsx";
import { followWith } from "./Link.tsx";
import { Heading } from "./Text.tsx";
const styles = {
  card: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1r0x45p",
    k7Eaqz: "xeuugli",
    kmVPX3: "x1wg0vgl x1i54pd5",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xv9mh3h",
    kaIpWk: "x1gzjth6",
    $$css: true
  },
  interactive: {
    kWkggS: "x40k5rp x14uvmqf",
    kVAM5u: "xv9mh3h x1wnwtbm",
    kMwMTN: "x1heor9g",
    kMnn75: "xkrqix3",
    k1ekBW: "xts7igz",
    kIyJzY: "x149xavy",
    $$css: true
  }
};
export type CardVariant = "plain" | "interactive" | "inset";
export interface CardProps {
  readonly title?: string;
  /** The title's place in the page outline. */
  readonly level?: 2 | 3 | 4;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly variant?: CardVariant;
  /** Required for `interactive`: where the whole card leads. An interactive card holds no `actions` or `footer`, since a link cannot contain controls. */
  readonly href?: string;
  readonly navigate?: Navigate;
  readonly testId?: string;
}
export function Card({
  title,
  level = 3,
  actions,
  children,
  footer,
  variant = "plain",
  href,
  navigate,
  testId
}: CardProps) {
  const titleId = useId();
  const head = title === undefined && actions === undefined ? null : <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x626il7">
        {title === undefined ? null : <div className="x12lumcd xeuugli">
            <Heading level={level} size="sectionTitle" id={titleId}>
              {title}
            </Heading>
          </div>}
        {actions === undefined ? null : <div className="x78zum5 x1a02dak x1173whf">{actions}</div>}
      </div>;
  if (variant === "interactive" && href !== undefined) return <a href={href} onClick={followWith(navigate, href)} data-testid={testId} {...stylex.props(styles.card, styles.interactive, focusRing)}>
        <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x626il7">
          {title === undefined ? null : <Heading level={level} size="label" id={titleId}>
              {title}
            </Heading>}
          <span aria-hidden="true" className="x78zum5 xvc5jky xyjz3t7">
            <ChevronRightIcon />
          </span>
        </div>
        <div className="xeuugli">{children}</div>
      </a>;
  const body = <>
      {head}
      <div className="xeuugli">{children}</div>
      {footer === undefined ? null : <div className="x78zum5 x1a02dak x626il7 xeonxcd x153ygsw x13fuv20 x9wgt4q">{footer}</div>}
    </>;
  return <section aria-labelledby={title === undefined ? undefined : titleId} data-testid={testId} {...{
    0: {
      className: "x78zum5 xdt5ytf x1r0x45p xeuugli x1wg0vgl x1i54pd5 xdq7b4a x1y0btm7 xv9mh3h x1gzjth6 x40k5rp"
    },
    1: {
      className: "x78zum5 xdt5ytf x1r0x45p xeuugli x1wg0vgl x1i54pd5 xdq7b4a x1y0btm7 xv9mh3h x1gzjth6 x14ba300"
    }
  }[!!(variant === "inset") << 0]}>
      {body}
    </section>;
}